<script lang="ts">
  import {
    type CalendarEvent,
    type CalendarDay,
    type LaneAssignable,
    CalendarCellInteraction,
    getSingleDayEvents,
    getSpanTypeClass,
    assignLanes,
    maxLaneCount,
    MAX_VISIBLE_EVENTS,
    MONTH_NAMES,
    WEEKDAY_LABELS,
    eventColor,
    eventEndDate,
    eventTotalDays,
    isMultiDayEvent,
    getTodayStr,
    formatEventTimeShort,
    hoveredDateStr,
    getWeekDates,
    cellClickMode,
    openEventModalForEdit,
  } from '../lib/calendarViewHelpers.svelte';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    year: number;
    events: CalendarEvent[];
    eventsByDate: Map<string, CalendarEvent[]>;
  }
  let { year, events, eventsByDate }: Props = $props();
  const ci = new CalendarCellInteraction(() => events);

  const SPAN_ROW_HEIGHT = 10;
  const DAY_HEADER_HEIGHT = 12;

  interface DayInfo extends CalendarDay {
    weekdayLabel: string;
  }
  interface MonthData {
    month: number;
    name: string;
    daysInMonth: number;
    days: DayInfo[];
  }
  interface MonthSpan extends LaneAssignable {
    event: CalendarEvent;
    continuesLeft: boolean;
    continuesRight: boolean;
  }

  // ── Data computation ───────────────────────────────────────────────────────

  function computeMonths(yr: number, ebd: Map<string, CalendarEvent[]>): MonthData[] {
    const todayStr = getTodayStr();
    return Array.from({ length: 12 }, (_unused, m) => {
      const daysInMonth = new Date(yr, m + 1, 0).getDate();
      const days: DayInfo[] = Array.from({ length: daysInMonth }, (_, d) => {
        const date = new Date(yr, m, d + 1);
        const dateStr = `${yr}-${String(m + 1).padStart(2, '0')}-${String(d + 1).padStart(2, '0')}`;
        const weekday = date.getDay();
        return {
          day: d + 1,
          dateStr,
          weekday,
          weekdayLabel: WEEKDAY_LABELS[weekday],
          isToday: dateStr === todayStr,
          isWeekend: weekday === 0 || weekday === 6,
          events: ebd.get(dateStr) || [],
        };
      });
      return { month: m, name: MONTH_NAMES[m], daysInMonth, days };
    });
  }

  let months = $derived(computeMonths(year, eventsByDate));

  function getMonthSpans(monthData: MonthData): MonthSpan[] {
    const m = monthData.month;
    const monthStart = `${year}-${String(m + 1).padStart(2, '0')}-01`;
    const monthEnd = `${year}-${String(m + 1).padStart(2, '0')}-${String(monthData.daysInMonth).padStart(2, '0')}`;

    const spans: MonthSpan[] = events
      .filter((e) => isMultiDayEvent(e) && e.date <= monthEnd && eventEndDate(e) >= monthStart)
      .map((e) => {
        const eStartDate = e.date < monthStart ? monthStart : e.date;
        const eEndDate = eventEndDate(e) > monthEnd ? monthEnd : eventEndDate(e);
        const startDay = parseInt(eStartDate.split('-')[2], 10);
        const endDay = parseInt(eEndDate.split('-')[2], 10);
        return {
          event: e,
          startCol: startDay - 1,
          endCol: endDay - 1,
          lane: 0,
          continuesLeft: e.date < monthStart,
          continuesRight: eventEndDate(e) > monthEnd,
        };
      });

    return assignLanes(spans);
  }

  // ── Week highlight ─────────────────────────────────────────────────────────

  let weekDates = $derived($hoveredDateStr ? getWeekDates($hoveredDateStr) : new Set<string>());
</script>

<div class="sequential-container">
  <div class="grid-row header-row">
    <div class="month-label-cell"></div>
    {#each Array.from({ length: 31 }, (_, i) => i + 1) as d}
      <div class="col-header">{d}</div>
    {/each}
  </div>

  {#each months as monthData}
    {@const spans = getMonthSpans(monthData)}
    {@const laneCount = maxLaneCount(spans)}
    {@const spanPadding = laneCount * SPAN_ROW_HEIGHT}
    <div class="grid-row">
      <div class="month-label-cell">
        <span class="month-name">{monthData.name}</span>
      </div>
      {#each Array.from({ length: 31 }, (_, i) => i) as colIdx}
        {#if colIdx < monthData.daysInMonth}
          {@const day = monthData.days[colIdx]}
          {@const singleDayEvents = getSingleDayEvents(day.events)}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div
            class="day-cell"
            class:today={day.isToday}
            class:weekend={day.isWeekend}
            class:drop-target={ci.dropTargetDate === day.dateStr}
            class:week-highlight={weekDates.has(day.dateStr)}
            class:hovered-cell={$hoveredDateStr === day.dateStr}
            onclick={() => {
              if ($cellClickMode === 'single') ci.cellClick(day.dateStr);
            }}
            ondblclick={() => {
              if ($cellClickMode === 'double') ci.cellClick(day.dateStr);
            }}
            onmouseenter={(e) =>
              ci.cellEnter(
                e,
                day.dateStr,
                `${day.weekdayLabel} ${monthData.name} ${day.day}`,
                day.events,
              )}
            onmouseleave={ci.cellLeave}
            ondragover={(e) => ci.cellDragOver(e, day.dateStr)}
            ondragleave={ci.cellDragLeave}
            ondrop={(e) => ci.cellDrop(e, day.dateStr)}
          >
            <span class="day-header">
              <span class="day-weekday">{day.weekdayLabel}</span>
              <span class="day-number">{day.day}</span>
            </span>
            {#if spanPadding > 0}
              <div class="span-spacer" style="height: {spanPadding}px;"></div>
            {/if}
            {#if singleDayEvents.length > 0}
              <div class="event-pills">
                {#each singleDayEvents.slice(0, MAX_VISIBLE_EVENTS) as event (event.id)}
                  {@const timeStr = formatEventTimeShort(event)}
                  <button
                    class="event-pill"
                    style="border-left-color: {eventColor(event.type)}"
                    class:dragging={ci.draggingEventId === event.id}
                    draggable={!$viewport.isMobile}
                    ondragstart={(e) => ci.pillDragStart(e, event)}
                    ondragend={ci.pillDragEnd}
                    onclick={(e) => {
                      e.stopPropagation();
                      openEventModalForEdit(event);
                    }}
                  >
                    {#if timeStr}<span class="event-time">{timeStr}</span>{/if}{event.title}
                  </button>
                {/each}
                {#if singleDayEvents.length > MAX_VISIBLE_EVENTS}
                  <span class="event-more">+{singleDayEvents.length - MAX_VISIBLE_EVENTS} more</span
                  >
                {/if}
              </div>
            {/if}
          </div>
        {:else}
          <div class="day-cell empty"></div>
        {/if}
      {/each}

      <!-- Spanning bars overlaid on the month row -->
      {#if spans.length > 0}
        <div
          class="spanning-layer"
          style="grid-column: 2 / -1; grid-row: 1; top: {DAY_HEADER_HEIGHT}px; height: {spanPadding}px;"
        >
          {#each spans as span (span.event.id)}
            <button
              type="button"
              class="spanning-bar {getSpanTypeClass(span.event.type)}"
              class:continues-left={span.continuesLeft}
              class:continues-right={span.continuesRight}
              class:dragging={ci.draggingEventId === span.event.id}
              draggable={!$viewport.isMobile}
              ondragstart={(e) => ci.pillDragStart(e, span.event)}
              ondragend={ci.pillDragEnd}
              onclick={(e) => {
                e.stopPropagation();
                ci.spanClick(span.event);
              }}
              style="
                top: {span.lane * SPAN_ROW_HEIGHT}px;
                left: calc({span.startCol} / 31 * 100%);
                width: calc({span.endCol - span.startCol + 1} / 31 * 100%);
              "
              title="{span.event.title} ({eventTotalDays(span.event)} days)"
            >
              <span class="spanning-title">{span.event.title}</span>
            </button>
          {/each}
        </div>
      {/if}
    </div>
  {/each}
</div>

<style>
  @import '../lib/calendarCellStyles.css';

  .sequential-container {
    width: calc(100% - 1rem);
    height: calc(100% - 1rem);
    margin: 0.5rem;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 1px;
    background: #e5e7eb;
    border-radius: 0.375rem;
    box-sizing: border-box;
  }

  .grid-row {
    display: grid;
    grid-template-columns: 1.5rem repeat(31, 1fr);
    gap: 1px;
    min-height: 0;
    flex: 1;
    position: relative;
  }

  .header-row {
    flex: 0 0 auto;
    min-height: 1.25rem;
  }

  .month-label-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    padding: 0.125rem;
    overflow: hidden;
  }

  .month-name {
    font-size: 0.5625rem;
    font-weight: 800;
    color: #374151;
    letter-spacing: 0.05em;
    writing-mode: vertical-lr;
    transform: rotate(180deg);
    white-space: nowrap;
  }

  .col-header {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    font-size: 0.5625rem;
    font-weight: 600;
    color: #6b7280;
  }

  .day-header {
    display: flex;
    align-items: baseline;
    gap: 0.125rem;
    line-height: 1.1;
  }
  .day-weekday {
    font-size: 0.5rem;
    font-weight: 500;
    color: #9ca3af;
    text-transform: uppercase;
  }
  .day-number {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #374151;
  }
</style>
